/**
 * DesignShapes: renders the decorative-wall layer in the 3D tunnel.
 *
 * A DesignShape is expanded (sampleDesignShape) into concrete wall transforms
 * and drawn with INSTANCING — one InstancedMesh per (shape + color + selected)
 * bucket, so the whole decorative layer costs roughly one draw call per group
 * regardless of how many walls each group repeats/mirrors into.
 *
 * The instances live INSIDE <BeatScrollGroup> (like obstacles/flash events), so
 * each instance sits at a static z = -beat * BEAT_SPACING and the parent group's
 * per-frame translation scrolls them past the camera — no per-frame work here.
 *
 * Design reuses the game obstacle FBX shapes AND the obstacle-style material
 * (createDesignMaterial) so a decorative wall looks exactly like a real obstacle.
 * Hovering an instance selects its group and opens the Shapes panel.
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useFBX, useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import type { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import { useEditorStore } from '../../store/editorStore';
import { OBSTACLE_SHAPE_FBX } from '../../store/editorStore';
import type { ObstacleShape } from '../../store/editorSettings';
import { BEAT_SPACING, mergeFbxGeometry, chartBeat } from './TunnelScene';
import { PERF } from '../../perf/adaptiveBudget';
import { createDesignMaterial, getObstacleMergedGeometry } from './ObstacleModel';
import { samplePrimaryInstances, sampleMeshInstances, gradientParams, sampleCustomPath, DEFAULT_CUSTOM_PATH, designAnimActive, applyDesignAnim, noteReactionEnvelope, designSegmentOffset, PRIMARY_MESH_ID } from '../../types/designShape';
import type { DesignMeshInstance, DesignGradientDir, DesignPathNode, DesignAnimConfig } from '../../types/designShape';
import { getSrFrequencyData } from '../../audio/srAnalyser';

// Must match TunnelScene / ObstacleModel — decor sits in the same vertical frame.
const NOTE_Y_OFFSET = 1.8;

// ── Merged-geometry cache (one BufferGeometry per shape FBX, shared by buckets) ──

const _geomCache = new Map<string, THREE.BufferGeometry>();

// regular / diagonal / center render correctly straight from the FBX (their root
// matrix is identity). Square / Triangle are NOT built here — their FBX carry a
// root rotation that `mergeFbxGeometry` bakes in (laying them flat); instead they
// reuse `getObstacleMergedGeometry` (raw extractGeometries + a clean +90° X, the
// same pipeline the VR obstacles use to stand them upright).
function getMergedGeometry(path: string, fbx: THREE.Group): THREE.BufferGeometry | null {
  const cached = _geomCache.get(path);
  if (cached) return cached;
  const merged = mergeFbxGeometry(fbx);
  if (!merged) return null;
  merged.computeVertexNormals();
  merged.computeBoundingSphere();
  _geomCache.set(path, merged);
  return merged;
}

// ── One instanced draw for a (shape + color + selected) bucket ───────────────

interface Bucket {
  shape: ObstacleShape;
  color: string;
  selected: boolean;
  wireframe: boolean;        // group render style — edges-only vs solid fill
  instances: DesignMeshInstance[];
  owners: string[];          // group id per instance (for hover → select)
  anim: DesignAnimConfig | null;          // live animation (null = static bucket)
  animCenter: [number, number, number];  // the art's placed centre (originX, originY, beat)
}

const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _euler = new THREE.Euler();
// Reused output for the per-frame animation pass (avoid per-instance allocation).
const _animOut: DesignMeshInstance = { x: 0, y: 0, beat: 0, scale: 1, scaleZ: 1, rotation: 0, rotationX: 0, rotationY: 0 };

// ── Animation driver ──────────────────────────────────────────────────────────
// Computed ONCE per frame (deduped by the render clock) and shared by every
// animated bucket: the current beat, a smoothed music-energy envelope from the
// live analyser, and how many beats it's been since the most recent note hit
// (`noteDt` — each bucket shapes its own attack/release envelope from it, and a
// per-segment stagger can offset it). A short history ring of the music energy
// lets a staggered Music reaction read a delayed sample per segment. Non-reactive
// store reads only.
const _MUSIC_HIST = 256;
const _musicHist = new Float32Array(_MUSIC_HIST);
let _musicHead = 0;
// Golden ratio — advancing the distribution "front" by this each beat lands it in
// the largest not-yet-covered gap (a low-discrepancy sequence), so a staggered
// reaction rotates through fresh segments each pulse instead of repeating.
const _GOLDEN = 0.6180339887498949;
const _drive = { key: -1, music: 0, noteDt: Infinity, beat: 0, secPerBeat: 0.5, dt: 1 / 60, beatIndex: 0, prevNoteDt: 0 };
function _noteDt(beat: number): number {
  const st = useEditorStore.getState();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const notes: Array<{ time_beats: number }> | undefined = (st.beatmap?.difficulties as any)?.[st.activeDifficulty]?.notes;
  if (!notes || !notes.length) return Infinity;
  // Notes are stored ascending by time — binary-search the last one at/before now.
  let lo = 0, hi = notes.length - 1, idx = -1;
  while (lo <= hi) { const m = (lo + hi) >> 1; if (notes[m].time_beats <= beat) { idx = m; lo = m + 1; } else hi = m - 1; }
  if (idx < 0) return Infinity;
  return beat - notes[idx].time_beats;   // beats since the hit (>= 0)
}
/** Music energy delayed by `offsetSec` seconds (read from the history ring) — for
 *  a staggered Music reaction so loud moments ripple across the segments. */
function _musicDelayed(offsetSec: number): number {
  if (offsetSec <= 0) return _drive.music;
  const framesBack = Math.min(_MUSIC_HIST - 1, Math.round(offsetSec / Math.max(1e-3, _drive.dt)));
  const idx = (_musicHead - framesBack + _MUSIC_HIST * 2) % _MUSIC_HIST;
  return _musicHist[idx];
}
function _updateDesignDrive(elapsed: number, delta: number) {
  if (_drive.key === elapsed) return;   // once per frame (elapsedTime is unique)
  _drive.key = elapsed;
  if (delta > 0) _drive.dt = delta;
  const st = useEditorStore.getState();
  const playing = st.playback?.playing ?? false;
  _drive.beat = st.playback?.currentBeat ?? 0;

  let noteDt: number;
  if (!playing) {
    // EDIT-MODE PREVIEW — the song isn't playing, so there's no live audio energy
    // and the beat is frozen. Simulate ONE BEAT PER SECOND off the render clock so
    // Music- and Notes-reactive animations still play while paused. (Time sync
    // already runs off the clock, so it's unaffected.)
    const beatPhase = elapsed - Math.floor(elapsed);   // 0..1 within the sim beat
    noteDt = beatPhase;                                 // a "note" hit every second
    _drive.secPerBeat = 1;                              // 1 sim beat == 1 second
    _drive.music = Math.pow(1 - beatPhase, 1.6);        // a decaying thump each beat
  } else {
    _drive.secPerBeat = 60 / (st.beatmap?.bpm || 120);
    // Music energy — low-mid bins (most musical energy), fast attack / slow release.
    const data = getSrFrequencyData();
    let e = 0;
    if (data) {
      let sum = 0; const lo = 2, hi = 64;
      for (let b = lo; b < hi; b++) sum += data[b];
      e = Math.min(1, (sum / ((hi - lo) * 255)) * 1.7);
    }
    _drive.music += (e - _drive.music) * (e > _drive.music ? 0.6 : 0.12);
    noteDt = _noteDt(_drive.beat);
  }
  _musicHead = (_musicHead + 1) % _MUSIC_HIST;
  _musicHist[_musicHead] = _drive.music;
  // Beat edge: noteDt drops when a new note/sim-beat starts → advance the rotating
  // distribution index so the staggered reaction covers a fresh region next beat.
  if (noteDt < _drive.prevNoteDt - 1e-4) _drive.beatIndex++;
  _drive.prevNoteDt = noteDt;
  _drive.noteDt = noteDt;
}

// ── Design pick registry ──────────────────────────────────────────────────────
// Every rendered decor InstancedMesh registers here (with a live ref to its
// instanceId→groupId map) so TunnelScene's native selection handler can raycast
// the ACTUAL wall geometry — precise for any wall size, unlike centre-proximity.
interface DesignMeshEntry { mesh: THREE.InstancedMesh; ownersRef: { current: string[] } }
const _designMeshReg = new Set<DesignMeshEntry>();

/** Raycast the registered decor meshes; return the group id of the nearest hit
 *  wall, or null. The caller sets the raycaster from the camera + pointer. */
export function pickDesignAt(raycaster: THREE.Raycaster): string | null {
  let bestId: string | null = null;
  let bestDist = Infinity;
  for (const e of _designMeshReg) {
    const hits = raycaster.intersectObject(e.mesh, false);
    for (const h of hits) {
      if (h.instanceId != null && h.distance < bestDist) {
        const id = e.ownersRef.current[h.instanceId];
        if (id) { bestDist = h.distance; bestId = id; }
      }
    }
  }
  return bestId;
}

function DesignBucket({ shape, color, selected, wireframe, instances, owners, anim, animCenter }: Bucket) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const obstacleScale = gp.obstacleScale;
  // Beat→depth spacing (== the module BEAT_SPACING, which SceneContent only sets
  // on the first useFrame). Read the reactive store value directly so the instance
  // matrices are placed with the correct depth from the FIRST render — otherwise
  // a refresh renders them with the default spacing (2) until a re-render (e.g.
  // selecting one) recomputes the matrices, which read as "compressed" in depth.
  const njs = useEditorStore(s => s.noteJumpSpeed);
  const shapeDef = OBSTACLE_SHAPE_FBX[shape];
  const fbx = useFBX(shapeDef.fbx);
  const geom = useMemo(
    // Square/Triangle: use the RAW geometry (extractGeometries, no baked FBX root
    // matrix) with NO rotation. The FBX child geometry is authored upright already
    // (thin on Z → face in the XY plane, facing the camera); it was mergeFbxGeometry
    // baking the root matrix that laid them flat, not the geometry itself.
    () => (shape === 'square' || shape === 'triangle')
      ? getObstacleMergedGeometry(shape, fbx, 0)
      : getMergedGeometry(shapeDef.fbx, fbx),
    [shapeDef.fbx, fbx, shape],
  );
  // The game's per-shape outline atlas → fed as the material's emissiveMap so
  // each wall shows the same glowing outline as the real obstacles.
  const outline = useTexture(shapeDef.texture);
  useMemo(() => { if (outline) { outline.colorSpace = THREE.SRGBColorSpace; outline.needsUpdate = true; } }, [outline]);
  const material = useMemo(
    () => createDesignMaterial(color, gp, selected, outline, wireframe),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [color, selected, wireframe, outline, gp.obstacleFillOpacity, gp.obstacleFresnelColor, gp.obstacleFresnelOpacity, gp.obstacleFresnelIntensity],
  );
  // Dispose the material when it's replaced / unmounted (we own it).
  useLayoutEffect(() => () => material.dispose(), [material]);

  const meshRef = useRef<THREE.InstancedMesh>(null);
  const count = instances.length;
  // Per-instance SMOOTHED reaction value (music/notes) — the animated intensity
  // eases toward its target each frame instead of snapping, so when the reaction
  // drops or a note retriggers, each wall continues from its CURRENT value rather
  // than jumping back to the default pose. Sized lazily to the instance count.
  const reactionRef = useRef<Float32Array | null>(null);
  // Per-instance FOV-cull state for STATIC buckets (1 = hidden past the horizon).
  // We only rewrite a matrix when it flips, so most frames touch nothing.
  const culledRef = useRef<Uint8Array | null>(null);

  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i];
      _pos.set(inst.x, NOTE_Y_OFFSET + inst.y, -inst.beat * njs);
      _euler.set(
        (inst.rotationX * Math.PI) / 180,
        (inst.rotationY * Math.PI) / 180,
        (inst.rotation * Math.PI) / 180,
      );
      _q.setFromEuler(_euler);
      const s = obstacleScale * inst.scale;
      // scaleZ stretches ONLY the local depth axis (compose applies scale in the
      // mesh's local frame, so it follows the wall's own rotation).
      _scl.set(s, s, s * (inst.scaleZ ?? 1));
      _m4.compose(_pos, _q, _scl);
      mesh.setMatrixAt(i, _m4);
    }
    mesh.instanceMatrix.needsUpdate = true;
    mesh.count = count;
    mesh.computeBoundingSphere();   // keep the raycast bounds in sync with the instances
    culledRef.current = null;       // re-evaluate the FOV cull against the new matrices
  }, [instances, obstacleScale, geom, count, njs]);

  // FOV cull for STATIC buckets (animated buckets cull inside their own writer).
  // Only rewrites an instance's matrix when it crosses the obstacle horizon, so a
  // static bucket stays near-zero cost; decor walls follow the obstacle distance.
  useFrame(() => {
    if (anim && anim.preset !== 'none') return;   // animated path handles its own cull
    const mesh = meshRef.current;
    if (!mesh) return;
    const cb = chartBeat();
    const view = PERF.obstacleViewBeats;
    let culled = culledRef.current;
    if (!culled || culled.length !== instances.length) {
      culled = new Uint8Array(instances.length);
      culledRef.current = culled;
    }
    let changed = false;
    for (let i = 0; i < instances.length; i++) {
      const inst = instances[i];
      const hide = (inst.beat - cb) > view ? 1 : 0;
      if (hide === culled[i]) continue;
      culled[i] = hide as number;
      changed = true;
      if (hide) {
        _m4.makeScale(0, 0, 0);
      } else {
        _pos.set(inst.x, NOTE_Y_OFFSET + inst.y, -inst.beat * njs);
        _euler.set((inst.rotationX * Math.PI) / 180, (inst.rotationY * Math.PI) / 180, (inst.rotation * Math.PI) / 180);
        _q.setFromEuler(_euler);
        const s = obstacleScale * inst.scale;
        _scl.set(s, s, s * (inst.scaleZ ?? 1));
        _m4.compose(_pos, _q, _scl);
      }
      mesh.setMatrixAt(i, _m4);
    }
    if (changed) mesh.instanceMatrix.needsUpdate = true;
  });

  // Live animation: when this bucket's group has an active preset, recompute the
  // instance matrices every frame from the shared driver (music/notes/time).
  // Static buckets (anim == null) skip this entirely — zero per-frame cost.
  useFrame((state, delta) => {
    if (!anim || anim.preset === 'none') return;
    const mesh = meshRef.current;
    if (!mesh) return;
    _updateDesignDrive(state.clock.elapsedTime, delta);
    const t = state.clock.elapsedTime * anim.speed;
    const [cx, cy, cb] = animCenter;
    const sync = anim.sync;
    // Beat-reaction envelope + distribution params (with back-compat defaults).
    const attack = anim.attack ?? 0.04, release = anim.release ?? 0.45;
    const easeIn = anim.easeIn ?? 1.5, easeOut = anim.easeOut ?? 2.2;
    const dist = anim.dist ?? 'uniform', stagger = anim.stagger ?? 0;
    const timePulse = 0.5 + 0.5 * Math.sin(t);   // 'time' → steady LFO for pump presets
    // The rotating distribution front for THIS beat — advances by the golden ratio
    // each pulse so a fresh, not-yet-covered band of segments leads (organic, never
    // resets to the same scatter). Only the discrete Notes reaction uses it.
    const front = (_drive.beatIndex * _GOLDEN) % 1;
    const n = instances.length;
    // Smoothing: ease each wall's reaction toward its target so it never snaps back
    // to the default pose — a dropping / retriggering reaction continues from its
    // CURRENT value. Asymmetric + frame-rate aware: fast attack (~20ms) keeps the
    // punch, slow release (~120ms) hides the reset (same idea as the music
    // envelope-follower). Time sync isn't smoothed (its signal is already smooth).
    const reactive = sync === 'notes' || sync === 'music';
    let react = reactionRef.current;
    if (reactive && (!react || react.length !== n)) { react = new Float32Array(n); reactionRef.current = react; }
    const d = Math.max(1e-3, delta);
    const kUp = 1 - Math.exp(-d / 0.02);     // fast rise — preserves the hit
    const kDown = 1 - Math.exp(-d / 0.12);   // slow fall — no snap back to default
    const ease = (i: number, target: number): number => {
      react![i] += (target - react![i]) * (target > react![i] ? kUp : kDown);
      return react![i];
    };
    const _viewBeats = PERF.obstacleViewBeats;
    for (let i = 0; i < n; i++) {
      const base = instances[i];
      // FOV cull: hide instances past the obstacle draw distance (decor walls
      // follow the obstacle horizon). _drive.beat == the offset-adjusted beat.
      if (base.beat - _drive.beat > _viewBeats) {
        _m4.makeScale(0, 0, 0);
        mesh.setMatrixAt(i, _m4);
        continue;
      }
      // Per-instance reaction — the distribution staggers WHEN each segment reacts,
      // so it no longer moves as one bar. Time sync is uniform (its spatial variety
      // comes from the preset's own Spread).
      let driveI: number, pulseI: number;
      if (sync === 'notes' && react) {
        const off = designSegmentOffset(dist, i, n, base.x - cx, base.y - cy, stagger, front);
        const target = noteReactionEnvelope(_drive.noteDt - off, attack, release, easeIn, easeOut);
        driveI = pulseI = ease(i, target);   // ease from the current value (no snap)
      } else if (sync === 'music' && react) {
        const off = designSegmentOffset(dist, i, n, base.x - cx, base.y - cy, stagger);
        const target = off > 0 ? _musicDelayed(off * _drive.secPerBeat) : _drive.music;
        driveI = pulseI = ease(i, target);
      } else {
        driveI = 1; pulseI = timePulse;
      }
      applyDesignAnim(base, anim, t, driveI, pulseI, cx, cy, cb, _animOut);
      _pos.set(_animOut.x, NOTE_Y_OFFSET + _animOut.y, -_animOut.beat * njs);
      _euler.set((_animOut.rotationX * Math.PI) / 180, (_animOut.rotationY * Math.PI) / 180, (_animOut.rotation * Math.PI) / 180);
      _q.setFromEuler(_euler);
      const s = obstacleScale * _animOut.scale;
      _scl.set(s, s, s * (_animOut.scaleZ ?? 1));
      _m4.compose(_pos, _q, _scl);
      mesh.setMatrixAt(i, _m4);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  // Keep a live ref to this bucket's instanceId→groupId map for the pick registry.
  const ownersRef = useRef(owners);
  ownersRef.current = owners;

  // Register this mesh so the native selection handler can raycast it.
  useLayoutEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const entry: DesignMeshEntry = { mesh, ownersRef };
    _designMeshReg.add(entry);
    return () => { _designMeshReg.delete(entry); document.body.style.cursor = ''; };
  }, []);

  // Selection (click + box-select) is handled centrally in TunnelScene's native
  // BoxSelectHandler (raycast for click, screen-projection for the marquee) so
  // it never races with the canvas's own pointer handling. We only add a hover
  // cursor here as an affordance that a wall is clickable.
  const onOver = (e: ThreeEvent<PointerEvent>) => {
    if (useEditorStore.getState().toolMode === 'select' && e.instanceId != null) {
      document.body.style.cursor = 'pointer';
    }
  };
  const onOut = () => { document.body.style.cursor = ''; };

  if (!geom || count === 0) return null;

  return (
    <instancedMesh
      // Re-create the buffer when the instance count changes.
      key={count}
      ref={meshRef}
      args={[geom, material, count]}
      frustumCulled={false}
      onPointerOver={onOver}
      onPointerOut={onOut}
    />
  );
}

// ── Colour gradient helper ───────────────────────────────────────────────────
// Quantised (16-stop) lerp between two hex colours so a gradient group buckets
// into a bounded number of colour buckets (one instanced draw per stop) instead
// of needing per-instance vertex colours.
const GRADIENT_STOPS = 16;
function lerpHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.replace('#', ''), 16);
  const pb = parseInt(b.replace('#', ''), 16);
  const ar = (pa >> 16) & 255, ag = (pa >> 8) & 255, ab = pa & 255;
  const br = (pb >> 16) & 255, bg = (pb >> 8) & 255, bb = pb & 255;
  const r = Math.round(ar + (br - ar) * t);
  const g = Math.round(ag + (bg - ag) * t);
  const bl = Math.round(ab + (bb - ab) * t);
  return '#' + ((1 << 24) | (r << 16) | (g << 8) | bl).toString(16).slice(1);
}

// ── Design layer: bucket every group's walls by shape/color/selected ──────────

export default function DesignShapes() {
  const designShapes = useEditorStore(s => s.designShapes);
  const selectedDesignShapeIds = useEditorStore(s => s.selectedDesignShapeIds);

  const buckets = useMemo(() => {
    const sel = new Set(selectedDesignShapeIds);
    const map = new Map<string, Bucket>();
    // A wall art is one or more LAYERS — the primary shape plus any extra
    // `parts` riding along at the same slots — each bucketed the same way so a
    // composite wall art (e.g. Regular + Diagonal combined) renders as one unit.
    const addLayer = (
      ownerId: string, shape: ObstacleShape, color: string, gradient: boolean, colorEnd: string,
      instances: DesignMeshInstance[], selected: boolean, wireframe: boolean, dir: DesignGradientDir,
      anim: DesignAnimConfig | null, animCenter: [number, number, number],
    ) => {
      const n = instances.length;
      const endCol = colorEnd || color;
      // Distribution parameter per wall (by order / axis / radial). Quantised to
      // a few stops so each colour is one instanced draw.
      const ts = gradient ? gradientParams(instances, dir) : null;
      for (let i = 0; i < n; i++) {
        // Gradient: the outline colour fades from `color` to `colorEnd` along the
        // chosen distribution.
        let col = color;
        if (gradient && n > 1 && ts) {
          const step = Math.round(ts[i] * GRADIENT_STOPS) / GRADIENT_STOPS;
          col = lerpHex(color, endCol, step);
        }
        // Animated groups get their OWN buckets (keyed by group id) so each bucket
        // carries a single anim config + centre and animates coherently; static
        // groups keep sharing colour/shape buckets as before.
        const animKey = anim ? ownerId : '';
        const key = `${shape}|${col}|${selected ? 1 : 0}|${wireframe ? 'w' : 's'}|${animKey}`;
        let b = map.get(key);
        if (!b) {
          b = { shape, color: col, selected, wireframe, instances: [], owners: [], anim, animCenter };
          map.set(key, b);
        }
        b.instances.push(instances[i]);
        b.owners.push(ownerId);
      }
    };
    for (const g of designShapes) {
      const selected = sel.has(g.id);
      // Wireframe is PER SHAPE (primary → g.style, each part → part.style), so one
      // art can mix solid + wireframe. The editor RESPECTS the chosen style — the
      // Quest 500-solid-wall cap is only a heads-up (panel warning), not a forced
      // override, so Solid always previews as solid (and stays correct for PCVR).
      const dir = g.gradientDir ?? 'order';
      // Live animation is per-shape when the shapes are DISCONNECTED: the primary
      // uses g.anim; each part uses its own part.anim (falling back to g.anim). When
      // ANCHORED, every shape shares g.anim and pivots around ONE centre (the group
      // origin) so the composite moves as a unit; disconnected shapes pivot around
      // their own placed position (origin + their offset).
      const groupCenter: [number, number, number] = [g.originX, g.originY, g.beat];
      const disc = !!g.shapesIndependent;
      const primaryAnim = designAnimActive(g) ? g.anim! : null;
      const resolveAnim = (a?: DesignAnimConfig) => (a && a.preset !== 'none') ? a : null;
      // Each layer (primary + every part) hides independently via its own
      // `hidden` flag — skip the whole addLayer call so a hidden shape doesn't
      // even get an instance slot (no wasted draw / no click target).
      if (!g.hidden) addLayer(g.id, g.shape, g.color, g.gradient, g.colorEnd, samplePrimaryInstances(g), selected, g.style === 'wireframe', dir, primaryAnim, groupCenter);
      for (const part of g.parts || []) {
        if (part.hidden) continue;
        const partAnim = disc ? resolveAnim(part.anim ?? g.anim) : primaryAnim;
        const partCenter: [number, number, number] = disc
          ? [g.originX + part.offsetX, g.originY + part.offsetY, g.beat + part.offsetBeat]
          : groupCenter;
        addLayer(g.id, part.shape, part.color, part.gradient || false, part.colorEnd || '', sampleMeshInstances(g, part), selected, part.style === 'wireframe', dir, partAnim, partCenter);
      }
    }
    return Array.from(map.entries());
  }, [designShapes, selectedDesignShapeIds]);

  if (buckets.length === 0) return null;

  return (
    <>
      {buckets.map(([key, b]) => (
        <DesignBucket key={key} {...b} />
      ))}
    </>
  );
}

// ── Move-gizmo for the active shape (primary or a Part) of the selected wall
// art — centred on THAT shape's actual mesh position, so grabbing an arrow
// moves exactly the shape you're editing in the panel. Mirrors the note/
// obstacle gizmo's screen-delta drag approach (see TunnelScene's GizmoArrow)
// but keeps its own isolated drag state — decor edits go through the decor
// store actions, not the note/slide ones. ─────────────────────────────────────

const _designGizmoDrag = {
  active: false,
  axis: '' as 'x' | 'y' | 'z' | 'xy',   // 'xy' = free plane drag (custom-path node handle)
  groupId: '',
  partId: null as string | null,   // null = dragging the PRIMARY shape (originX/Y/beat)
  nodeIndex: null as number | null, // non-null = dragging a custom-path control node
  startMouseX: 0,
  startMouseY: 0,
  startX: 0, startY: 0, startBeat: 0,
};

function _designGizmoMove(e: PointerEvent) {
  if (!_designGizmoDrag.active) return;
  const dx = e.clientX - _designGizmoDrag.startMouseX;
  const dy = e.clientY - _designGizmoDrag.startMouseY;
  const store = useEditorStore.getState();
  const { groupId, partId, nodeIndex, axis } = _designGizmoDrag;

  // Custom-path node drag → move the control node in local (x, y, z=beat) space.
  // 'xy' (grabbing the sphere) moves both x and y at once; the coloured arrows
  // constrain to a single axis (z drags the node's beat offset).
  if (nodeIndex != null) {
    const patch: Partial<DesignPathNode> = {};
    if (axis === 'x' || axis === 'xy') patch.x = Math.round((_designGizmoDrag.startX + dx * 0.012) * 100) / 100;
    if (axis === 'y' || axis === 'xy') patch.y = Math.round((_designGizmoDrag.startY + (-dy) * 0.012) * 100) / 100;
    if (axis === 'z') patch.z = Math.round((_designGizmoDrag.startBeat + (-dy) * 0.02) * 100) / 100;
    store.updateDesignNode(groupId, nodeIndex, patch);
    return;
  }

  const applyPrimary = (patch: { originX?: number; originY?: number; beat?: number }) => store.updateDesignShape(groupId, patch);
  const applyPart = (patch: { offsetX?: number; offsetY?: number; offsetBeat?: number }) => {
    if (partId) store.updateDesignMesh(groupId, partId, patch);
  };

  if (_designGizmoDrag.axis === 'x') {
    const newX = Math.round((_designGizmoDrag.startX + dx * 0.012) * 100) / 100;
    partId ? applyPart({ offsetX: newX }) : applyPrimary({ originX: newX });
  } else if (_designGizmoDrag.axis === 'y') {
    const newY = Math.round((_designGizmoDrag.startY + (-dy) * 0.012) * 100) / 100;
    partId ? applyPart({ offsetY: newY }) : applyPrimary({ originY: newY });
  } else {
    // Z axis — a continuous small nudge in beats (fine adjustment lives in the
    // panel's Move Z / Offset Z steppers).
    const newBeat = Math.round((_designGizmoDrag.startBeat + (-dy) * 0.02) * 100) / 100;
    partId ? applyPart({ offsetBeat: newBeat }) : applyPrimary({ beat: Math.max(0, newBeat) });
  }
}
function _designGizmoUp() {
  if (!_designGizmoDrag.active) return;
  _designGizmoDrag.active = false;
  document.body.style.cursor = '';
  window.removeEventListener('pointermove', _designGizmoMove);
  window.removeEventListener('pointerup', _designGizmoUp);
}

/** Whether a decor gizmo drag is in progress — TunnelScene's native
 *  BoxSelectHandler checks this (alongside the note/obstacle `_gizmoDrag`) so
 *  it doesn't ALSO arm a selection marquee under the same mousedown/mouseup,
 *  which would otherwise replace the decor selection with an empty box-select
 *  result the instant you release the gizmo (kicking the panel back to the
 *  Shape Designer list mid-drag). */
export function isDesignGizmoDragActive(): boolean {
  return _designGizmoDrag.active;
}

function DesignGizmoArrow({ axis, color, direction, groupId, partId, nodeIndex = null, start }: {
  axis: 'x' | 'y' | 'z'; color: string; direction: [number, number, number];
  groupId: string; partId: string | null; nodeIndex?: number | null;
  start: { x: number; y: number; beat: number };
}) {
  const lineLen = 0.45, coneLen = 0.12, coneRad = 0.045, lineRad = 0.012, hitRad = 0.12;
  const hitLen = lineLen + coneLen;
  const rot: [number, number, number] = axis === 'y' ? [0, 0, 0] : axis === 'x' ? [0, 0, -Math.PI / 2] : [Math.PI / 2, 0, 0];
  const conePos: [number, number, number] = [direction[0] * (lineLen + coneLen / 2), direction[1] * (lineLen + coneLen / 2), direction[2] * (lineLen + coneLen / 2)];
  const linePos: [number, number, number] = [direction[0] * lineLen / 2, direction[1] * lineLen / 2, direction[2] * lineLen / 2];
  const hitPos: [number, number, number] = [direction[0] * hitLen / 2, direction[1] * hitLen / 2, direction[2] * hitLen / 2];

  const handleDown = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    _designGizmoDrag.active = true;
    _designGizmoDrag.axis = axis;
    _designGizmoDrag.groupId = groupId;
    _designGizmoDrag.partId = partId;
    _designGizmoDrag.nodeIndex = nodeIndex;
    _designGizmoDrag.startMouseX = e.nativeEvent.clientX;
    _designGizmoDrag.startMouseY = e.nativeEvent.clientY;
    _designGizmoDrag.startX = start.x;
    _designGizmoDrag.startY = start.y;
    _designGizmoDrag.startBeat = start.beat;
    document.body.style.cursor = 'grabbing';
    window.addEventListener('pointermove', _designGizmoMove);
    window.addEventListener('pointerup', _designGizmoUp);
  };

  return (
    <group>
      <mesh position={linePos} rotation={rot} renderOrder={21}>
        <cylinderGeometry args={[lineRad, lineRad, lineLen, 6]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      <mesh position={conePos} rotation={rot} renderOrder={21}>
        <coneGeometry args={[coneRad, coneLen, 8]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      <mesh
        position={hitPos} rotation={rot} renderOrder={22}
        onPointerDown={handleDown}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = 'grab'; }}
        onPointerOut={() => { if (!_designGizmoDrag.active) document.body.style.cursor = ''; }}
      >
        <cylinderGeometry args={[hitRad, hitRad, hitLen, 8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
      </mesh>
    </group>
  );
}

/** Renders the move-gizmo for whichever shape (primary or a Part) is active in
 *  the Shape Designer panel — mounted OUTSIDE BeatScrollGroup (like the note/
 *  obstacle gizmos) and self-positions every frame via chartBeat(). */
export function DesignMeshGizmo() {
  const shapeDesignerOpen = useEditorStore(s => s.shapeDesignerOpen);
  const selectedIds = useEditorStore(s => s.selectedDesignShapeIds);
  const activePartId = useEditorStore(s => s.activeDesignMeshId);
  const designShapes = useEditorStore(s => s.designShapes);
  const groupRef = useRef<THREE.Group>(null);

  const groupId = selectedIds.length === 1 ? selectedIds[0] : null;
  const g = groupId ? designShapes.find(d => d.id === groupId) : undefined;
  const isPart = !!(activePartId && activePartId !== PRIMARY_MESH_ID);
  const part = isPart ? (g?.parts || []).find(p => p.id === activePartId) : null;
  const ready = shapeDesignerOpen && !!g && (!isPart || !!part);

  useFrame(() => {
    if (!groupRef.current) return;
    if (!ready || !g) { groupRef.current.visible = false; return; }
    const rawBeat = chartBeat();
    const x = g.originX + (part ? part.offsetX : 0);
    const y = g.originY + (part ? part.offsetY : 0);
    const beat = g.beat + (part ? part.offsetBeat : 0);
    groupRef.current.position.set(x, NOTE_Y_OFFSET + y, -(beat - rawBeat) * BEAT_SPACING);
    groupRef.current.visible = true;
  });

  if (!ready || !g) return null;

  // The value each axis actually WRITES to — the part's own offset, or the
  // group's origin/beat when editing the primary (no active part).
  const fieldStart = {
    x: part ? part.offsetX : g.originX,
    y: part ? part.offsetY : g.originY,
    beat: part ? part.offsetBeat : g.beat,
  };
  const partId = part ? part.id : null;

  return (
    <group ref={groupRef} renderOrder={20}>
      <DesignGizmoArrow axis="x" color="#FF4444" direction={[1, 0, 0]} groupId={g.id} partId={partId} start={fieldStart} />
      <DesignGizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} groupId={g.id} partId={partId} start={fieldStart} />
      <DesignGizmoArrow axis="z" color="#4488FF" direction={[0, 0, -1]} groupId={g.id} partId={partId} start={fieldStart} />
    </group>
  );
}

// ── Custom-curve path editor — the draggable control-node handles for a wall art
// whose curve is 'custom'. Shows a smooth preview of the Catmull-Rom path the
// walls follow, a sphere handle at each control node (grab to move it in the
// X/Y plane), and, for the selected node, a 3-axis arrow gizmo (X/Y move + Z to
// slide its beat offset). Mounted OUTSIDE BeatScrollGroup and self-scrolls each
// frame via chartBeat(), exactly like DesignMeshGizmo.
//
// Handles are drawn on the group's BASE path (before Expand/Global rotation and
// before mirror/kaleidoscope, which fan out extra copies) — you edit the source
// path and those effects transform the whole result. With no such effects (the
// common case) the handles sit precisely on the rendered walls. ───────────────

export function DesignPathEditor() {
  const open = useEditorStore(s => s.shapeDesignerOpen);
  const selectedIds = useEditorStore(s => s.selectedDesignShapeIds);
  const designShapes = useEditorStore(s => s.designShapes);
  const activeNode = useEditorStore(s => s.activeDesignNodeIndex);
  const setActiveNode = useEditorStore(s => s.setActiveDesignNodeIndex);
  const groupRef = useRef<THREE.Group>(null);

  const groupId = selectedIds.length === 1 ? selectedIds[0] : null;
  const g = groupId ? designShapes.find(d => d.id === groupId) : undefined;
  const ready = open && !!g && g.curve === 'custom';

  // The control nodes (seeded to the default starter path when a group just
  // switched to Custom and hasn't materialised its own path yet).
  const path: DesignPathNode[] = (g && g.customPath && g.customPath.length >= 2) ? g.customPath : DEFAULT_CUSTOM_PATH;
  const sx = g?.spreadX || 0, sy = g?.spreadY || 0, sz = g?.spreadZ || 0;

  // Node local-space position → parent-group local (the parent already carries
  // originX/Y + the scroll for g.beat; z maps beats to depth via BEAT_SPACING).
  const toLocal = (px: number, py: number, pz: number): [number, number, number] =>
    [sx + px, sy + py, -(sz + pz) * BEAT_SPACING];

  // Smooth preview polyline of the sampled Catmull-Rom path.
  const lineObject = useMemo(() => {
    const pts = sampleCustomPath(path, 48).map(p => { const [x, y, z] = toLocal(p.x, p.y, p.z); return new THREE.Vector3(x, y, z); });
    const geo = new THREE.BufferGeometry().setFromPoints(pts);
    const mat = new THREE.LineBasicMaterial({ color: 0xFFD54A, transparent: true, opacity: 0.55, depthTest: false, depthWrite: false, fog: false });
    return new THREE.Line(geo, mat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, sx, sy, sz]);
  useEffect(() => () => { lineObject.geometry.dispose(); (lineObject.material as THREE.Material).dispose(); }, [lineObject]);

  useFrame(() => {
    const grp = groupRef.current;
    if (!grp) return;
    if (!ready || !g) { grp.visible = false; return; }
    grp.position.set(g.originX, NOTE_Y_OFFSET + g.originY, -(g.beat - chartBeat()) * BEAT_SPACING);
    grp.visible = true;
  });

  if (!ready || !g) return null;
  const gid = g.id;

  // Grab a node sphere → free-plane (xy) drag + select it (so its arrows appear).
  // Sets the shared drag flag synchronously so TunnelScene's BoxSelectHandler
  // (which reads isDesignGizmoDragActive on the same pointerdown) won't clear the
  // selection out from under us.
  const startNodeDrag = (i: number, node: DesignPathNode) => (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    setActiveNode(i);
    _designGizmoDrag.active = true;
    _designGizmoDrag.axis = 'xy';
    _designGizmoDrag.groupId = gid;
    _designGizmoDrag.partId = null;
    _designGizmoDrag.nodeIndex = i;
    _designGizmoDrag.startMouseX = e.nativeEvent.clientX;
    _designGizmoDrag.startMouseY = e.nativeEvent.clientY;
    _designGizmoDrag.startX = node.x;
    _designGizmoDrag.startY = node.y;
    _designGizmoDrag.startBeat = node.z;
    document.body.style.cursor = 'grabbing';
    window.addEventListener('pointermove', _designGizmoMove);
    window.addEventListener('pointerup', _designGizmoUp);
  };

  return (
    <group ref={groupRef} renderOrder={20}>
      <primitive object={lineObject} />
      {path.map((node, i) => {
        const start = { x: node.x, y: node.y, beat: node.z };
        return (
          <group key={i} position={toLocal(node.x, node.y, node.z)}>
            <mesh
              renderOrder={22}
              onPointerDown={startNodeDrag(i, node)}
              onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = 'grab'; }}
              onPointerOut={() => { if (!_designGizmoDrag.active) document.body.style.cursor = ''; }}
            >
              <sphereGeometry args={[i === activeNode ? 0.11 : 0.085, 16, 16]} />
              <meshBasicMaterial color={i === activeNode ? '#FFD54A' : '#FFFFFF'} transparent opacity={0.95} depthTest={false} depthWrite={false} fog={false} />
            </mesh>
            {i === activeNode && (
              <>
                <DesignGizmoArrow axis="x" color="#FF4444" direction={[1, 0, 0]} groupId={gid} partId={null} nodeIndex={i} start={start} />
                <DesignGizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} groupId={gid} partId={null} nodeIndex={i} start={start} />
                <DesignGizmoArrow axis="z" color="#4488FF" direction={[0, 0, -1]} groupId={gid} partId={null} nodeIndex={i} start={start} />
              </>
            )}
          </group>
        );
      })}
    </group>
  );
}
